export type Category = {
  id: number;
  name: string;
  plaidPrimary: string;
  type?: string | null;
  isBudget?: boolean;
  householdId?: string | null;
};
export type CategoryItem = { id: number | null; name: string; isCustom?: boolean };
export type CategorySection = { title: string; data: CategoryItem[] };

/** plaidPrimary group key for household custom categories. */
export const CUSTOM_CATEGORY_PRIMARY = 'CUSTOM';

/** Convert FOOD_AND_DRINK -> "Food & Drink" */
function primaryLabel(key: string): string {
  if (key === CUSTOM_CATEGORY_PRIMARY) return 'My Categories';
  return key
    .replace(/_AND_/g, ' & ')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Section order optimized for recategorization — common spending first, income/transfers last. */
const SECTION_ORDER: string[] = [
  CUSTOM_CATEGORY_PRIMARY,
  'FOOD_AND_DRINK',
  'GENERAL_MERCHANDISE',
  'TRANSPORTATION',
  'RENT_AND_UTILITIES',
  'ENTERTAINMENT',
  'GENERAL_SERVICES',
  'MEDICAL',
  'PERSONAL_CARE',
  'HOME_IMPROVEMENT',
  'TRAVEL',
  'GOVERNMENT_AND_NON_PROFIT',
  'LOAN_PAYMENTS',
  'BANK_FEES',
  'OTHER',
  'TRANSFER_OUT',
  'TRANSFER_IN',
  'LOAN_DISBURSEMENTS',
  'INCOME',
];

export function buildCategorySections(
  categories: Category[],
  selectedId: number | null,
  nullOptionLabel = 'Uncategorized',
): CategorySection[] {
  const groupMap = new Map<string, CategoryItem[]>();
  for (const category of categories) {
    let group = groupMap.get(category.plaidPrimary);
    if (!group) {
      group = [];
      groupMap.set(category.plaidPrimary, group);
    }
    group.push({
      id: category.id,
      name: category.name,
      isCustom: category.plaidPrimary === CUSTOM_CATEGORY_PRIMARY,
    });
  }

  const sections: CategorySection[] = [];

  for (const key of SECTION_ORDER) {
    const items = groupMap.get(key);
    if (items) sections.push({ title: primaryLabel(key), data: items });
  }

  for (const [key, items] of groupMap) {
    if (!SECTION_ORDER.includes(key)) {
      sections.push({ title: primaryLabel(key), data: items });
    }
  }

  sections.push({ title: '', data: [{ id: null, name: nullOptionLabel }] });

  return sections;
}

/** Filter category sections by search string — matches on category name or section title. */
export function filterCategorySections(
  sections: CategorySection[],
  search: string,
): CategorySection[] {
  if (!search) return sections;

  const query = search.toLowerCase();
  const filtered: CategorySection[] = [];

  for (const section of sections) {
    const matchingItems = section.data.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        section.title.toLowerCase().includes(query),
    );

    if (matchingItems.length > 0) {
      filtered.push({ ...section, data: matchingItems });
    }
  }

  return filtered;
}
